import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {useAbrirVersionNueva} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {translateApiError} from '@/lib/errors'

/** H08: abre la versión siguiente de un perfil confirmado; la vigente sigue exigible hasta que se firme la nueva. */
export function AlcanceCambioDialog({
  businessId,
  positionId,
  disabled,
  confirmado,
}: {
  businessId: string
  positionId: string
  disabled?: boolean
  /**
   * El diálogo vive fuera del bloque «confirmado»: abrir la versión nueva vuelve borrador el perfil
   * y, montado adentro, el aviso de éxito se desmontaría en el mismo render.
   */
  confirmado: boolean
}) {
  const {t} = useTranslation()
  const [abierto, setAbierto] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const abrir = useAbrirVersionNueva(businessId, positionId)
  return (
    <div className="space-y-2">
      {confirmado && (
        <Button
          variant="outline"
          size="sm"
          data-testid="perfil-cambiar"
          disabled={disabled || abrir.isPending}
          onClick={() => {
            setError(null)
            setAviso(null)
            setAbierto(true)
          }}>
          {t('perfilCambio.abrir')}
        </Button>
      )}
      {aviso && (
        <p className="notice-green rounded-lg px-3 py-2 text-sm" data-testid="perfil-cambio-aviso">
          {aviso}
        </p>
      )}
      {error && (
        <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="perfil-cambio-error">
          {error}
        </p>
      )}
      <ConfirmDialog
        open={abierto}
        onOpenChange={setAbierto}
        title={t('perfilCambio.titulo')}
        description={t('perfilCambio.queda')}
        porque={{
          label: t('perfilCambio.porqueLabel'),
          testId: 'perfil-cambio-porque',
          contenidoTestId: 'perfil-cambio-porque-contenido',
          contenido: (
            <ul className="list-disc space-y-1 pl-4">
              <li>{t('perfilCambio.porqueCopia')}</li>
              <li>{t('perfilCambio.nadaSeGuardaSinAlcance')}</li>
              <li>{t('perfilCambio.noSeRecalifica')}</li>
            </ul>
          ),
        }}
        confirmLabel={t('perfilCambio.confirmarAbrir')}
        cancelLabel={t('common.cancel')}
        pending={abrir.isPending}
        onConfirm={() =>
          abrir.mutate(undefined, {
            onSuccess: (data) => {
              setAbierto(false)
              setAviso(
                `${t('perfilCambio.editandoVersion', {version: data.version, anterior: data.version_anterior})} ${t(
                  'perfilCambio.copiadas',
                  {count: data.lineas}
                )}`
              )
            },
            onError: (err) => {
              setAbierto(false)
              setError(translateApiError(err))
            },
          })
        }
      />
    </div>
  )
}
